import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/AdminDashboard.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    api_model: 'claude-3-5-sonnet-20241022',
    temperature: 0.7,
    api_endpoint: 'https://api.anthropic.com/v1/messages',
    max_tokens_ideas: 2000,
    max_tokens_article: 4000,
    prompt_ideas: '',
    prompt_article: '',
    api_key: ''
  });

  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('model');
  const [error, setError] = useState('');

  // Default prompts from the app
  const DEFAULT_PROMPT_IDEAS = `Tu es un expert SEO senior. Nous sommes en {NOW_DATE}. Réponds UNIQUEMENT avec un objet JSON valide, sans markdown.

Génère 10 idées d'articles SEO à fort potentiel pour {NOW_YEAR}.
Activité : {activity} | Site : {website} | Mots-clés : {keywords} | Pays : {country} | Langue : {language}
Varie les intentions (informationnelle, commerciale, transactionnelle, navigationnelle) et difficultés (Facile/Moyen/Difficile).

Format de sortie : JSON avec la structure suivante :
{
  "ideas": [
    {
      "title": "...",
      "keyword": "...",
      "difficulty": "Facile|Moyen|Difficile",
      "intent": "Informationnelle|Commerciale|Transactionnelle|Navigationnelle",
      "why": "raison stratégique en 1 phrase"
    }
  ]
}`;

  const DEFAULT_PROMPT_ARTICLE = `Tu es expert SEO senior avec 10+ années d'expérience. Rédige un article RÉFÉRENCE optimisé Google ET moteurs IA.

Sujet : "{title}"
Mot-clé : "{keyword}"
Pays : {country} | Ton : {tone}
Longueur : {length} mots minimum

⚠️ RÈGLES STRICTES (NON-NÉGOCIABLES) :

1. AUCUNE affirmation sans source vérifiable
2. Ajouter 2-3 TABLEAUX citables
3. Ajouter 1 FRAMEWORK CLAIR et citables
4. Ajouter 3-5 EXEMPLES CONCRETS vérifiables
5. CITER LES SOURCES explicitement
6. TON PERSONNEL + RETOURS TERRAIN
7. STRUCTURES ULTRA-CITABLES PAR LES IA

STRUCTURE OBLIGATOIRE :
<h1> Titre accrocheur avec mot-clé
<p> Intro (2-3 phrases) + contexte
<h2> Section 1 + <h3> sous-sections avec TABLEAU ou LISTE
<h2> Section 2 + <h3> sous-sections avec EXEMPLE concret
<h2> Section 3 + <h3> sous-sections avec FRAMEWORK
<h2> Section 4 + <h3> sous-sections avec TABLEAU ou BENCHMARK
<h2>FAQ</h2> (5 questions avec réponses sourcées)
<h2>Conclusion</h2> (résumé + call-to-action)

Format : HTML pur UNIQUEMENT, rien d'autre.`;

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }

    // Fetch current settings
    fetchSettings();
  }, [navigate]);

  const fetchSettings = async () => {
    try {
      const response = await fetch('/api/get-settings.php');
      const data = await response.json();
      
      if (data.success && data.settings) {
        // If prompts are empty, use defaults
        setSettings({
          ...data.settings,
          prompt_ideas: data.settings.prompt_ideas || DEFAULT_PROMPT_IDEAS,
          prompt_article: data.settings.prompt_article || DEFAULT_PROMPT_ARTICLE
        });
      }
    } catch (err) {
      setError('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('adminToken');
    
    try {
      const response = await fetch('/api/update-settings.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      const data = await response.json();
      
      if (data.success) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        setError(data.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    }
  };

  const handleReset = (field) => {
    if (field === 'prompt_ideas') {
      setSettings({ ...settings, prompt_ideas: DEFAULT_PROMPT_IDEAS });
    } else if (field === 'prompt_article') {
      setSettings({ ...settings, prompt_article: DEFAULT_PROMPT_ARTICLE });
    } else if (field === 'all') {
      setSettings({
        ...settings,
        api_model: 'claude-3-5-sonnet-20241022',
        temperature: 0.7,
        api_endpoint: 'https://api.anthropic.com/v1/messages',
        max_tokens_ideas: 2000,
        max_tokens_article: 4000,
        prompt_ideas: DEFAULT_PROMPT_IDEAS,
        prompt_article: DEFAULT_PROMPT_ARTICLE
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin');
  };

  if (loading) {
    return <div className="admin-loading">Chargement...</div>;
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="admin-header-content">
          <div className="admin-title">
            <h1>Panneau d'Administration</h1>
            <p>Configurez les paramètres de l'IA et les prompts</p>
          </div>
          <div className="admin-actions">
            {saved && <span className="saved-indicator">✓ Sauvegardé</span>}
            <button onClick={handleSave} className="btn-save">
              Sauvegarder
            </button>
            <button onClick={handleLogout} className="btn-logout">
              Déconnexion
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="admin-error">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      <div className="admin-tabs">
        <button 
          className={activeTab === 'model' ? 'active' : ''}
          onClick={() => setActiveTab('model')}
        >
          Configuration du Modèle
        </button>
        <button 
          className={activeTab === 'prompts' ? 'active' : ''}
          onClick={() => setActiveTab('prompts')}
        >
          Prompts
        </button>
        <button 
          className={activeTab === 'api' ? 'active' : ''}
          onClick={() => setActiveTab('api')}
        >
          Clé API
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'model' && (
          <div className="settings-section">
            <h2>Configuration du Modèle IA</h2>
            
            <div className="setting-group">
              <label>Modèle Claude API</label>
              <select 
                value={settings.api_model} 
                onChange={(e) => setSettings({...settings, api_model: e.target.value})}
              >
                <option value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet (Latest)</option>
                <option value="claude-3-opus-20240229">Claude 3 Opus</option>
                <option value="claude-3-sonnet-20240229">Claude 3 Sonnet</option>
                <option value="claude-3-haiku-20240307">Claude 3 Haiku (Fast)</option>
                <option value="claude-2.1">Claude 2.1</option>
              </select>
              <span className="setting-info">Sélectionnez le modèle Claude à utiliser</span>
            </div>

            <div className="setting-group">
              <label>Temperature (Créativité)</label>
              <div className="temperature-control">
                <input 
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={settings.temperature}
                  onChange={(e) => setSettings({...settings, temperature: parseFloat(e.target.value)})}
                />
                <span className="temperature-value">{settings.temperature}</span>
              </div>
              <span className="setting-info">0 = Plus factuel, 1 = Plus créatif (Recommandé: 0.7)</span>
            </div>

            <div className="setting-group">
              <label>Max Tokens - Idées</label>
              <input 
                type="number"
                value={settings.max_tokens_ideas}
                onChange={(e) => setSettings({...settings, max_tokens_ideas: parseInt(e.target.value)})}
                min="500"
                max="4000"
              />
              <span className="setting-info">Nombre maximum de tokens pour la génération d'idées</span>
            </div>

            <div className="setting-group">
              <label>Max Tokens - Articles</label>
              <input 
                type="number"
                value={settings.max_tokens_article}
                onChange={(e) => setSettings({...settings, max_tokens_article: parseInt(e.target.value)})}
                min="1000"
                max="8000"
              />
              <span className="setting-info">Nombre maximum de tokens pour la génération d'articles</span>
            </div>
          </div>
        )}

        {activeTab === 'prompts' && (
          <div className="settings-section">
            <h2>Configuration des Prompts</h2>
            
            <div className="setting-group">
              <div className="prompt-header">
                <label>Prompt pour la génération d'idées</label>
                <button onClick={() => handleReset('prompt_ideas')} className="btn-reset">
                  Réinitialiser
                </button>
              </div>
              <textarea
                value={settings.prompt_ideas}
                onChange={(e) => setSettings({...settings, prompt_ideas: e.target.value})}
                rows={12}
                placeholder="Entrez le prompt pour générer les idées d'articles..."
              />
              <span className="setting-info">
                Variables disponibles: {'{NOW_DATE}'}, {'{NOW_YEAR}'}, {'{activity}'}, {'{website}'}, {'{keywords}'}, {'{country}'}, {'{language}'}
              </span>
            </div>

            <div className="setting-group">
              <div className="prompt-header">
                <label>Prompt pour la génération d'articles</label>
                <button onClick={() => handleReset('prompt_article')} className="btn-reset">
                  Réinitialiser
                </button>
              </div>
              <textarea
                value={settings.prompt_article}
                onChange={(e) => setSettings({...settings, prompt_article: e.target.value})}
                rows={15}
                placeholder="Entrez le prompt pour générer les articles complets..."
              />
              <span className="setting-info">
                Variables: {'{title}'}, {'{keyword}'}, {'{country}'}, {'{tone}'}, {'{length}'}, {'{audience}'}, {'{niche}'}
              </span>
            </div>
          </div>
        )}

        {activeTab === 'api' && (
          <div className="settings-section">
            <h2>Configuration de la Clé API</h2>
            
            <div className="setting-group">
              <label>Clé API Claude (Anthropic)</label>
              <div className="api-key-input">
                <input 
                  type="text"
                  value={settings.api_key}
                  onChange={(e) => setSettings({...settings, api_key: e.target.value})}
                  placeholder="sk-ant-api03-..."
                  className="api-key-field"
                />
              </div>
              <span className="setting-info">
                Votre clé API Anthropic pour Claude. Obtenez-la sur console.anthropic.com
              </span>
            </div>

            <div className="setting-group">
              <label>Endpoint API</label>
              <input 
                type="text"
                value={settings.api_endpoint}
                onChange={(e) => setSettings({...settings, api_endpoint: e.target.value})}
                placeholder="https://api.anthropic.com/v1/messages"
              />
              <span className="setting-info">URL de l'endpoint API (ne pas modifier sauf cas spécial)</span>
            </div>

            <div className="danger-zone">
              <h3>Zone de danger</h3>
              <button onClick={() => handleReset('all')} className="btn-danger">
                Réinitialiser tous les paramètres
              </button>
              <span className="setting-info warning">
                ⚠️ Cette action réinitialisera tous les paramètres à leurs valeurs par défaut
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="admin-footer">
        <div className="footer-info">
          <span>PowArticle Admin v1.0</span>
          <span className="separator">•</span>
          <span>Connecté en tant que: admin</span>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;